import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { FiHome, FiLogOut, FiUser, FiBarChart2, FiChevronDown, FiChevronUp, FiFileText, FiMenu, FiX } from 'react-icons/fi';
import Logo1 from '../../assets/logo-5.png';

const API_BASE_URL = 'https://infrec.in/php-backend/backend/user';

const Navbar = ({ customerName = '' }) => {
  const [profileImage, setProfileImage] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showProducts, setShowProducts] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProfileImage = async () => {
      try {
        const customerData = sessionStorage.getItem('customerData');
        if (!customerData) return;

        const data = JSON.parse(customerData);
        if (!data.id) return;

        const response = await fetch(`${API_BASE_URL}/customerprofile.php?customer_id=${data.id}`);
        const result = await response.json();

        if (result.success && result.customer.profile_photo) {
          setProfileImage(result.customer.profile_photo);
        }
      } catch (err) {
        console.error('Error fetching profile image:', err);
      }
    };

    fetchProfileImage();
  }, []);

  const products = [
    { name: 'Life Insurance', path: '/products/life-insurance', icon: '🛡️' },
    { name: 'Health Insurance', path: '/products/health-insurance', icon: '🏥' },
    { name: 'Mutual Funds', path: '/products/mutual-funds', icon: '📊' },
    { name: 'Fixed Deposit', path: '/products/fixed-deposit', icon: '💳'},
    { name: 'Motor Insurance', path: '/products/motor-insurance', icon: '🚗'}
  ];

  const handleLogout = () => {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('customerData');
    sessionStorage.removeItem('profileImage');
    navigate('/customer-login');
  };

  const handleHome = () => {
    navigate('/');
  };

  return (
    <nav className="sticky top-0 z-40" style={{backgroundColor: '#305daa'}}>
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo/Brand */}
          <div className="flex items-center gap-3">
            <img className="w-auto h-10 sm:h-12 object-contain" src={Logo1} alt="Infrec Logo" />
          </div>

          {/* Desktop Navigation Items */}
          <div className="hidden md:flex items-center gap-8">
            {/* Profile Button */}
            <button
              onClick={() => navigate('/customer-profile')}
              className="flex items-center gap-2 text-white hover:opacity-80 font-medium transition-all"
            >
              <FiUser size={18} />
              <span>Profile</span>
            </button>

            {/* Dashboard Button */}
            <button
              onClick={() => navigate('/my-dashboard')}
              className="flex items-center gap-2 text-white hover:opacity-80 font-medium transition-all"
            >
              <FiBarChart2 size={18} />
              <span>My Dashboard</span>
            </button>

            {/* Home Button */}
            <button
              onClick={handleHome}
              className="flex items-center gap-2 text-white hover:opacity-80 font-medium transition-all"
            >
              <FiHome size={18} />
              <span>Home</span>
            </button>

            {/* Products Dropdown */}
            <div className="relative group">
              <button
                className="flex items-center gap-2 text-white hover:opacity-80 font-medium transition-all"
              >
                <FiFileText size={18} />
                <span>Products</span>
                <FiChevronDown size={16} />
              </button>

              {/* Dropdown Menu */}
              <div className="absolute left-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-2 z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                {products.map((product) => (
                  <button
                    key={product.name}
                    onClick={() => {
                      navigate(product.path);
                    }}
                    className="w-full text-left px-4 py-2 text-gray-700 hover:bg-blue-50 transition-all flex items-center gap-2"
                    style={{color: '#305daa'}}
                  >
                    <span className="text-lg">{product.icon}</span>
                    <span>{product.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-red-500 hover:opacity-80 font-medium transition-all"
            >
              <FiLogOut size={18} />
              <span>Logout</span>
            </button>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-white hover:opacity-80 transition-all"
            >
              {mobileMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
            </button>
          </div>

          {/* Profile Section (Right side - Desktop only) */}
          <div className="hidden md:flex items-center gap-4">
            <div className="relative">
              {profileImage ? (
                <div className="w-12 h-12 rounded-full overflow-hidden border-2 cursor-pointer hover:shadow-lg transition-shadow" style={{borderColor: 'white'}}>
                  <img
                    src={profileImage}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg border-2 cursor-pointer hover:shadow-lg transition-shadow"
                  style={{backgroundColor: 'rgba(255,255,255,0.2)', borderColor: 'white'}}
                >
                  {customerName && customerName.charAt(0).toUpperCase()}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 space-y-3 border-t border-blue-400 pt-4">
            {/* Profile Button */}
            <button
              onClick={() => {
                navigate('/customer-profile');
                setMobileMenuOpen(false);
              }}
              className="w-full flex items-center gap-2 text-white hover:opacity-80 font-medium transition-all px-4 py-2 rounded hover:bg-blue-600"
            >
              <FiUser size={18} />
              <span>Profile</span>
            </button>

            {/* Dashboard Button */}
            <button
              onClick={() => {
                navigate('/my-dashboard');
                setMobileMenuOpen(false);
              }}
              className="w-full flex items-center gap-2 text-white hover:opacity-80 font-medium transition-all px-4 py-2 rounded hover:bg-blue-600"
            >
              <FiBarChart2 size={18} />
              <span>Dashboard</span>
            </button>

            {/* Home Button */}
            <button
              onClick={() => {
                handleHome();
                setMobileMenuOpen(false);
              }}
              className="w-full flex items-center gap-2 text-white hover:opacity-80 font-medium transition-all px-4 py-2 rounded hover:bg-blue-600"
            >
              <FiHome size={18} />
              <span>Home</span>
            </button>

            {/* Products Dropdown Mobile */}
            <div className="px-4">
              <button
                onClick={() => setShowProducts(!showProducts)}
                className="w-full flex items-center gap-2 text-white hover:opacity-80 font-medium transition-all py-2 rounded hover:bg-blue-600"
              >
                <FiFileText size={18} />
                <span>Products</span>
                {showProducts ? <FiChevronUp size={16} className="ml-auto" /> : <FiChevronDown size={16} className="ml-auto" />}
              </button>

              {/* Products Submenu Mobile */}
              {showProducts && (
                <div className="mt-2 space-y-2 pl-4 border-l-2 border-blue-400">
                  {products.map((product) => (
                    <button
                      key={product.name}
                      onClick={() => {
                        navigate(product.path);
                        setMobileMenuOpen(false);
                        setShowProducts(false);
                      }}
                      className="w-full text-left px-4 py-2 text-white hover:bg-blue-600 rounded transition-all flex items-center gap-2"
                    >
                      <span className="text-lg">{product.icon}</span>
                      <span>{product.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Logout Button */}
            <button
              onClick={() => {
                handleLogout();
                setMobileMenuOpen(false);
              }}
              className="w-full flex items-center gap-2 text-red-300 hover:opacity-80 font-medium transition-all px-4 py-2 rounded hover:bg-red-600"
            >
              <FiLogOut size={18} />
              <span>Logout</span>
            </button>

            {/* Profile Image Mobile */}
            <div className="px-4 pt-2 border-t border-blue-400">
              <div className="flex items-center gap-3">
                {profileImage ? (
                  <div className="w-10 h-10 rounded-full overflow-hidden border-2" style={{borderColor: 'white'}}>
                    <img
                      src={profileImage}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm border-2"
                    style={{backgroundColor: 'rgba(255,255,255,0.2)', borderColor: 'white'}}
                  >
                    {customerName && customerName.charAt(0).toUpperCase()}
                  </div>
                )}
                <span className="text-white font-medium">{customerName}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;

Navbar.propTypes = {
  customerName: PropTypes.string
};
